/**
 * ScamBlocker AI — Ayarlar Sayfası (/settings)
 * Organizasyon ayarları: veri saklama, kayıt/rıza, marka, webhook (org_settings tablosu).
 * NOT (FAZ 5): Bu sayfa backend `orgPortal.js` GET/PATCH /api/org/settings kontratına
 * uyarlanmıştır — eski org_name/timezone/notify_email/sso_enabled/mfa_required gibi
 * backend'de karşılığı olmayan sahte alanlar kaldırılmıştır.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Settings } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';
import type { OrgSettings } from '@/shared/lib/api/types';
import styles from './SettingsPage.module.css';

export function SettingsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery<OrgSettings>({
    queryKey: ['org-settings'],
    queryFn: async () => {
      const { data } = await apiClient.get(API.org.settings);
      return data.settings ?? data;
    },
  });

  const [form, setForm] = useState<Partial<OrgSettings>>({});

  const effective = { ...(data ?? {}), ...form } as OrgSettings;

  const patch = <K extends keyof OrgSettings>(key: K, value: OrgSettings[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiClient.patch(API.org.settings, form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-settings'] });
      setForm({});
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const hasChanges = Object.keys(form).length > 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Settings size={22} className={styles.titleIcon} />
          <h1 className={styles.title}>{t('settings.title')}</h1>
        </div>
        {hasChanges && (
          <button
            type="button"
            className={styles.saveBtn}
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            <Save size={15} />
            {saveMutation.isPending ? t('settings.saving') : t('settings.save')}
          </button>
        )}
      </div>

      {saved && <div className={styles.successBanner}>{t('settings.savedSuccess')}</div>}
      {saveMutation.isError && <div className={styles.errMsg}>{t('settings.saveError')}</div>}

      {isLoading && (
        <div className={styles.center}>
          <span className={styles.spinner} />
        </div>
      )}

      {!isLoading && (
        <div className={styles.sections}>
          {/* Veri Saklama & Kayıt */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.dataRetention')}</h2>
            <div className={styles.card}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-retention-days">
                  {t('settings.retentionDays')}
                </label>
                <input
                  id="settings-retention-days"
                  className={styles.input}
                  type="number"
                  min={1}
                  max={3650}
                  value={effective.retention_days ?? 90}
                  onChange={(e) => patch('retention_days', Number(e.target.value))}
                  style={{ width: 120 }}
                />
              </div>

              <div className={styles.toggleRow}>
                <div>
                  <span className={styles.toggleLabel}>{t('settings.recordingEnabled')}</span>
                  <span className={styles.toggleDesc}>{t('settings.recordingEnabledDesc')}</span>
                </div>
                <button
                  type="button"
                  className={`${styles.toggle} ${effective.recording_enabled ? styles.on : ''}`}
                  onClick={() => patch('recording_enabled', !effective.recording_enabled)}
                  role="switch"
                  aria-checked={!!effective.recording_enabled}
                >
                  <span className={styles.toggleThumb} />
                </button>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-data-region">
                  {t('settings.dataRegion')}
                </label>
                <input
                  id="settings-data-region"
                  className={styles.input}
                  type="text"
                  placeholder="eu-west"
                  value={effective.data_region ?? ''}
                  onChange={(e) => patch('data_region', e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Açık Rıza (KVKK) */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.consent')}</h2>
            <div className={styles.card}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-consent-text">
                  {t('settings.consentText')}
                </label>
                <textarea
                  id="settings-consent-text"
                  className={styles.input}
                  rows={3}
                  value={effective.consent_text ?? ''}
                  onChange={(e) => patch('consent_text', e.target.value)}
                />
              </div>

              <div className={styles.toggleRow}>
                <div>
                  <span className={styles.toggleLabel}>{t('settings.consentIvrEnabled')}</span>
                  <span className={styles.toggleDesc}>{t('settings.consentIvrEnabledDesc')}</span>
                </div>
                <button
                  type="button"
                  className={`${styles.toggle} ${effective.consent_ivr_enabled ? styles.on : ''}`}
                  onClick={() => patch('consent_ivr_enabled', !effective.consent_ivr_enabled)}
                  role="switch"
                  aria-checked={!!effective.consent_ivr_enabled}
                >
                  <span className={styles.toggleThumb} />
                </button>
              </div>
            </div>
          </section>

          {/* Marka */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.branding')}</h2>
            <div className={styles.card}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-branding-name">
                  {t('settings.brandingName')}
                </label>
                <input
                  id="settings-branding-name"
                  className={styles.input}
                  type="text"
                  value={effective.branding_name ?? ''}
                  onChange={(e) => patch('branding_name', e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-branding-logo">
                  {t('settings.brandingLogoUrl')}
                </label>
                <input
                  id="settings-branding-logo"
                  className={styles.input}
                  type="url"
                  placeholder="https://sirketiniz.com/logo.png"
                  value={effective.branding_logo_url ?? ''}
                  onChange={(e) => patch('branding_logo_url', e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Legacy Webhook (org_settings.webhook_url) */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('settings.legacyWebhook')}</h2>
            <div className={styles.card}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-webhook-url">
                  {t('settings.webhookUrl')}
                </label>
                <input
                  id="settings-webhook-url"
                  className={styles.input}
                  type="url"
                  placeholder="https://api.sirketiniz.com/webhook"
                  value={effective.webhook_url ?? ''}
                  onChange={(e) => patch('webhook_url', e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="settings-webhook-secret">
                  {t('settings.webhookSecret')}
                </label>
                <input
                  id="settings-webhook-secret"
                  className={styles.input}
                  type="password"
                  placeholder={t('settings.webhookSecretPlaceholder')}
                  value={effective.webhook_secret ?? ''}
                  onChange={(e) => patch('webhook_secret', e.target.value)}
                />
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
